import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useState, useEffect } from "react";
import { reportsAPI } from "../../services/api";
import * as XLSX from "xlsx";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box, Typography, Card, CardContent, Table, TableBody,
  TextField, FormControl, InputLabel, Select, MenuItem,
  TableCell, TableContainer, TableHead, TableRow, Chip, Avatar,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, LinearProgress, Grid
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

export default function AgentProgress() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [error, setError] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { loadAgents(); }, []);

  const loadAgents = async () => {
    try {
      const res = await reportsAPI.getAllAgentsProgress();
      setAgents(res.data.agents);
    } catch { setError("Failed to load agent progress."); }
    finally { setLoading(false); }
  };

  const handleViewAttempts = async (agent) => {
    setSelectedAgent(agent);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await reportsAPI.getAgentAttempts(agent.user_id);
      setAttempts(res.data);
    } catch { setError("Failed to load attempts."); }
    finally { setDetailLoading(false); }
  };

  const readinessColor = {
    not_started: "default", not_ready: "default",
    in_progress: "warning", ready: "success",
    certified: "success", final_passed: "success"
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  const exportAllAgentsCSV = () => {
    const headers = ["Name","Email","Team","Employee #","Attempts","Passed","Failed","Avg Score","Modules Done","Modules Total","Status"];
    const rows = filteredAgents.map(a => [a.full_name, a.email, a.team, a.employee_number||"", a.total_attempts, a.passed_attempts, a.failed_attempts, a.average_score!==null?a.average_score+"%":"", a.modules_completed, a.modules_total, a.readiness_status.replace(/_/g," ")]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a"); el.href=url; el.download="agent_progress.csv"; el.click();
    URL.revokeObjectURL(url);
  };

  const exportAllAgentsExcel = () => {
    const rows = filteredAgents.map(a => ({"Name":a.full_name,"Email":a.email,"Team":a.team,"Employee #":a.employee_number||"","Attempts":a.total_attempts,"Passed":a.passed_attempts,"Failed":a.failed_attempts,"Avg Score":a.average_score!==null?a.average_score+"%":"","Modules Completed":a.modules_completed,"Modules Total":a.modules_total,"Status":a.readiness_status.replace(/_/g," ")}));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Agent Progress");
    XLSX.writeFile(wb, "agent_progress.xlsx");
  };

  const exportAllAgentsPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16); doc.setTextColor(26,35,126);
    doc.text("Synaptix - Agent Progress Report", 14, 20);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
    autoTable(doc, {
      startY: 35,
      head: [["Name","Team","Attempts","Passed","Avg Score","Modules","Status"]],
      body: filteredAgents.map(a=>[a.full_name,a.team||"-",a.total_attempts,a.passed_attempts,a.average_score!==null?a.average_score+"%":"-",`${a.modules_completed}/${a.modules_total}`,a.readiness_status.replace(/_/g," ")]),
      headStyles:{fillColor:[26,35,126]}, alternateRowStyles:{fillColor:[245,247,250]}, styles:{fontSize:9}
    });
    doc.save("agent_progress.pdf");
  };

  const exportAgentDetailCSV = () => {
    if (!attempts) return;
    const headers = ["Module","Exam","Attempt #","Score","Correct","Total","Result","Date","Duration (s)"];
    const rows = attempts.attempts.map(a=>[a.module_title,a.template_name,a.attempt_number,a.score!==null?a.score+"%":"",a.correct_answers||"",a.total_questions||"",a.passed===true?"PASSED":a.passed===false?"FAILED":a.status,a.submitted_at?new Date(a.submitted_at).toLocaleString():"",a.time_taken_seconds||""]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a"); el.href=url; el.download=`${attempts.agent_name.replace(" ","_")}_attempts.csv`; el.click();
    URL.revokeObjectURL(url);
  };

  const exportAgentDetailExcel = () => {
    if (!attempts) return;
    const rows = attempts.attempts.map(a=>({"Module":a.module_title,"Exam":a.template_name,"Attempt #":a.attempt_number,"Score":a.score!==null?a.score+"%":"","Correct":a.correct_answers||"","Total":a.total_questions||"","Result":a.passed===true?"PASSED":a.passed===false?"FAILED":a.status,"Date":a.submitted_at?new Date(a.submitted_at).toLocaleString():"","Duration (s)":a.time_taken_seconds||""}));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Exam Attempts");
    XLSX.writeFile(wb, `${attempts.agent_name.replace(" ","_")}_attempts.xlsx`);
  };

  const exportAgentDetailPDF = () => {
    if (!attempts) return;
    const doc = new jsPDF();
    doc.setFontSize(16); doc.setTextColor(26,35,126);
    doc.text(`Exam History - ${attempts.agent_name}`, 14, 20);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
    autoTable(doc, {
      startY: 35,
      head: [["Module","Exam","Attempt","Score","Result","Date"]],
      body: attempts.attempts.map(a=>[a.module_title,a.template_name,a.attempt_number,a.score!==null?a.score+"%":"-",a.passed===true?"PASSED":a.passed===false?"FAILED":a.status,a.submitted_at?new Date(a.submitted_at).toLocaleDateString():"-"]),
      headStyles:{fillColor:[26,35,126]}, alternateRowStyles:{fillColor:[245,247,250]}, styles:{fontSize:9}
    });
    doc.save(`${attempts.agent_name.replace(" ","_")}_attempts.pdf`);
  };

  const filteredAgents = agents.filter(a => {
    const matchSearch = !search || a.full_name.toLowerCase().includes(search.toLowerCase()) || a.email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || a.readiness_status === filterStatus;
    const matchTeam = !filterTeam || a.team === filterTeam;
    return matchSearch && matchStatus && matchTeam;
  });

  const teams = [...new Set(agents.map(a => a.team).filter(Boolean))];

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1a237e">Agent Progress</Typography>
          <Typography variant="body2" color="text.secondary">
            View all agent exam attempts and performance
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportAllAgentsCSV} disabled={filteredAgents.length === 0}>Export CSV</Button>
          <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={exportAllAgentsExcel} disabled={filteredAgents.length === 0} sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>Export Excel</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DownloadIcon />} onClick={exportAllAgentsPDF} disabled={filteredAgents.length === 0}>Export PDF</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField placeholder="Search by name or email..." value={search}
          onChange={e => setSearch(e.target.value)} size="small" sx={{ flexGrow: 1, minWidth: 220 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="not_started">Not Started</MenuItem>
            <MenuItem value="not_ready">Not Ready</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="ready">Ready</MenuItem>
            <MenuItem value="certified">Certified</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Team</InputLabel>
          <Select value={filterTeam} label="Team" onChange={e => setFilterTeam(e.target.value)}>
            <MenuItem value="">All Teams</MenuItem>
            {teams.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                  <TableCell><b>Agent</b></TableCell>
                  <TableCell><b>Team</b></TableCell>
                  <TableCell><b>Attempts</b></TableCell>
                  <TableCell><b>Passed</b></TableCell>
                  <TableCell><b>Failed</b></TableCell>
                  <TableCell><b>Avg Score</b></TableCell>
                  <TableCell><b>Progress</b></TableCell>
                  <TableCell><b>Readiness</b></TableCell>
                  <TableCell><b>Details</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAgents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" py={3}>
                        No agents found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredAgents.map(agent => (
                  <TableRow key={agent.user_id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: "#1a237e", fontSize: 12 }}>
                          {agent.full_name.split(" ").map(n => n[0]).join("")}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {agent.full_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {agent.employee_number}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={agent.team} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {agent.total_attempts}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={agent.passed_attempts}
                        color="success" size="small"
                        icon={<CheckCircleIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={agent.failed_attempts}
                        color={agent.failed_attempts > 0 ? "error" : "default"}
                        size="small"
                        icon={<CancelIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      {agent.average_score !== null ? (
                        <Chip
                          label={`${agent.average_score}%`}
                          color={agent.average_score >= 80 ? "success" : "warning"}
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption">
                            {agent.modules_completed}/{agent.modules_total} modules
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {agent.modules_total > 0
                              ? Math.round(agent.modules_completed / agent.modules_total * 100)
                              : 0}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={agent.modules_total > 0
                            ? Math.round(agent.modules_completed / agent.modules_total * 100)
                            : 0}
                          sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={agent.readiness_status.replace("_", " ")}
                        color={readinessColor[agent.readiness_status] || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewAttempts(agent)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Attempt Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>
          Exam History — {selectedAgent?.full_name}
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : attempts && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: "center", p: 2, bgcolor: "#f5f7fa", borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight={800} color="#1a237e">
                      {attempts.total_attempts}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Attempts
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: "center", p: 2, bgcolor: "#e8f5e9", borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight={800} color="#2e7d32">
                      {attempts.attempts.filter(a => a.passed).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Passed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: "center", p: 2, bgcolor: "#ffebee", borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight={800} color="#c62828">
                      {attempts.attempts.filter(a => a.passed === false).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Failed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: "center", p: 2, bgcolor: "#e3f2fd", borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight={800} color="#1565c0">
                      {attempts.attempts.filter(a => a.score).length > 0
                        ? Math.round(attempts.attempts
                            .filter(a => a.score)
                            .reduce((sum, a) => sum + a.score, 0) /
                            attempts.attempts.filter(a => a.score).length)
                        : 0}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Score
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {attempts.attempts.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={3}>
                  No exam attempts yet
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                        <TableCell><b>Module</b></TableCell>
                        <TableCell><b>Exam</b></TableCell>
                        <TableCell><b>Attempt</b></TableCell>
                        <TableCell><b>Score</b></TableCell>
                        <TableCell><b>Result</b></TableCell>
                        <TableCell><b>Date</b></TableCell>
                        <TableCell><b>Duration</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attempts.attempts.map(attempt => (
                        <TableRow key={attempt.attempt_id} hover
                          sx={{ bgcolor: attempt.passed === false ? "#fff8f8" : "inherit" }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {attempt.module_title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {attempt.template_name}
                            </Typography>
                            <Chip label={attempt.assessment_type} size="small"
                              sx={{ mt: 0.5 }} />
                          </TableCell>
                          <TableCell>
                            <Chip label={`#${attempt.attempt_number}`} size="small" />
                          </TableCell>
                          <TableCell>
                            {attempt.score !== null ? (
                              <Box>
                                <Typography variant="body2" fontWeight={700}
                                  color={attempt.score >= 80 ? "#2e7d32" : "#c62828"}>
                                  {attempt.score}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {attempt.correct_answers}/{attempt.total_questions} correct
                                </Typography>
                              </Box>
                            ) : (
                              <Chip label={attempt.status} size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {attempt.passed === true && (
                              <Chip label="PASSED" color="success" size="small"
                                icon={<CheckCircleIcon />} />
                            )}
                            {attempt.passed === false && (
                              <Chip label="FAILED" color="error" size="small"
                                icon={<CancelIcon />} />
                            )}
                            {attempt.passed === null && (
                              <Chip label={attempt.status} size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDate(attempt.submitted_at || attempt.started_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDuration(attempt.time_taken_seconds)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" startIcon={<DownloadIcon />} onClick={exportAgentDetailCSV} disabled={!attempts}>Export CSV</Button>
          <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={exportAgentDetailExcel} disabled={!attempts} sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>Export Excel</Button>
          <Button size="small" variant="outlined" color="error" startIcon={<DownloadIcon />} onClick={exportAgentDetailPDF} disabled={!attempts}>Export PDF</Button>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
